/**
 * Экран Кошко краша.
 *
 * Порядок блоков подчинён взгляду игрока: сверху история, затем поле с
 * котиком, сразу под ним кнопка ставки в зоне большого пальца, и только потом
 * список остальных игроков.
 */

import { useEffect, useState, type ReactNode } from "react";

import { multiplierToNumber } from "@koshkodrop/shared";

import { GameShell } from "../components/GameShell";
import { CrashCanvas } from "../games/crash/CrashCanvas";
import { BetPanel } from "../games/crash/BetPanel";
import { HistoryStrip } from "../games/crash/HistoryStrip";
import { PlayersList } from "../games/crash/PlayersList";
import { api } from "../lib/api";
import { useCrash } from "../store/crash";

interface Props {
  onBack: () => void;
}

export function CrashPage({ onBack }: Props) {
  const { connect, disconnect, connection, lastError, clearError, round } = useCrash();
  const [verifying, setVerifying] = useState<{
    sequence: number;
    commitHash: string;
    serverSeed: string | null;
    crashPoint: number | null;
  } | null>(null);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Ошибку показываем недолго: она мешает следующей ставке
  useEffect(() => {
    if (!lastError) return;
    const timer = setTimeout(clearError, 2600);
    return () => clearTimeout(timer);
  }, [lastError, clearError]);

  const openRound = async (sequence: number) => {
    try {
      const data = await api.verifyRound(sequence);
      setVerifying(data);
    } catch {
      // Раунд мог не успеть раскрыться — просто ничего не показываем
    }
  };

  return (
    <GameShell title="Кошко краш" subtitle="Котик бежит — успей забрать, пока не упал" onBack={onBack}>
      <HistoryStrip onSelect={openRound} />

      <CrashCanvas />

      {connection !== "open" && (
        <p className="rounded-2xl bg-night-850 px-4 py-2.5 text-center text-xs font-semibold text-white/45">
          {connection === "connecting" ? "Подключаюсь к раунду…" : "Соединение потеряно, пробую снова"}
        </p>
      )}

      {lastError && (
        <p className="animate-float-up rounded-2xl bg-lose-500/15 px-4 py-2.5 text-center text-sm font-semibold text-lose-400">
          {lastError.message}
        </p>
      )}

      <BetPanel />

      <PlayersList />

      {round && (
        <button
          type="button"
          onClick={() => void openRound(round.sequence)}
          className="w-full rounded-2xl border border-white/5 bg-night-850/50 px-4 py-3 text-left"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-white/35">
            Раунд №{round.sequence} · честная игра
          </p>
          <p className="mt-1 truncate font-mono text-[11px] text-white/45">
            хеш: {round.commitHash.slice(0, 32)}…
          </p>
        </button>
      )}

      {verifying && (
        <VerifyCard data={verifying} onClose={() => setVerifying(null)} />
      )}
    </GameShell>
  );
}

interface VerifyCardProps {
  data: {
    sequence: number;
    commitHash: string;
    serverSeed: string | null;
    crashPoint: number | null;
  };
  onClose: () => void;
}

/**
 * Карточка проверки честности. Показывает то, чем игрок может сам пересчитать
 * результат: хеш, опубликованный до ставок, и раскрытый после раунда сид.
 */
function VerifyCard({ data, onClose }: VerifyCardProps) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-end bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-float-up safe-bottom w-full space-y-3 rounded-3xl border border-white/10 bg-night-850 p-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-extrabold text-white">Раунд №{data.sequence}</h3>
          <button type="button" onClick={onClose} className="text-sm text-white/40">
            закрыть
          </button>
        </div>

        <Field label="Точка краша">
          {data.crashPoint === null ? (
            <span className="text-white/40">раунд ещё идёт</span>
          ) : (
            <span className="tabular font-extrabold text-paws-300">
              {multiplierToNumber(data.crashPoint).toFixed(2)}x
            </span>
          )}
        </Field>

        <Field label="Хеш до ставок">
          <span className="break-all font-mono text-[11px] text-white/60">{data.commitHash}</span>
        </Field>

        <Field label="Серверный сид">
          {data.serverSeed ? (
            <span className="break-all font-mono text-[11px] text-white/60">{data.serverSeed}</span>
          ) : (
            <span className="text-white/40">раскроется после раунда</span>
          )}
        </Field>

        <p className="text-[11px] leading-relaxed text-white/35">
          Хеш публикуется до приёма ставок, сид — после раунда. Проверьте сами:
          sha256 от сида должен совпасть с хешем, а точка краша считается из
          sha256(сид + «:crash»).
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-night-900 px-3.5 py-2.5">
      <p className="text-[10px] font-bold uppercase tracking-wide text-white/30">{label}</p>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}
